import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { Contacto } from '../../../app/private/shared/models/Contacto';
import { Preguntas } from '../../../app/private/shared/models/preguntas';
import { AZURE_CA_URLS } from '../../../app/shared/enums/HttpStatusCodes';
import { ContenidoClientes } from '../../../app/shared/models/ContenidoClientes';
import { GESTION_CONTENIDO_ENDPOINTS } from '../../shared/models/Constants';
import { SharedServiceService } from '../../shared/services/shared-service.service';
import {
    contactProgram,
    contentCustomersV1,
    errorContactProgram,
    errorContentCustomersV1,
    errorGetContactInfo,
    errorGetInicioApp,
    errorGetMechanicsProgram,
    errorGetMuralData,
    errorGetPrivacyNotice,
    errorGetQuestions,
    errorGetTermsAndConditions,
    errorRateOurAnswer,
    getContactInfo,
    getInicioApp,
    getMechanicsProgram,
    getMuralData,
    getPrivacyNotice,
    getQuestions,
    getTermsAndConditions,
    // New actions
    rateOurAnswer,
    successContactProgram,
    successContentCustomersV1,
    successGetContactInfo,
    successGetInicioApp,
    successGetMechanicsProgram,
    successGetMuralData,
    successGetPrivacyNotice,
    successGetQuestions,
    successGetTermsAndConditions,
    successRateOurAnswer
} from '../actions/content-management.actions';

@Injectable()
export class ContentManagementEffects {
    constructor(
        private actions$: Actions
        , private httpS: SharedServiceService
    ) { }

    public contactProgram$ = createEffect(
        () => this.actions$.pipe(
            ofType(contactProgram),
            switchMap(
                (actions) => this.httpS.executeRequestByUser(GESTION_CONTENIDO_ENDPOINTS.post_program_contact, { ...actions }, this.httpS.getAzureCAUrl(AZURE_CA_URLS.CONTENIDO))
                    .pipe(
                        map(resp => {
                            const data: Contacto[] = Object
                                .keys(resp)
                                .filter(key => !isNaN(Number(key)))
                                .map(key => resp[key]);

                            return successContactProgram({ data });
                        }),
                        catchError((err: Error) => of(errorContactProgram({ payload: err })))
                    )
            )
        )
    );

    public contentCustomersV1$ = createEffect(
        () => this.actions$.pipe(
            ofType(contentCustomersV1),
            switchMap(
                () => this.httpS.executeRequestByUser(GESTION_CONTENIDO_ENDPOINTS.post_customer_content, null, this.httpS.getAzureCAUrl(AZURE_CA_URLS.CONTENIDO))
                    .pipe(
                        map(resp => {

                            const data: ContenidoClientes[] = Object
                                .keys(resp)
                                .filter(key => !isNaN(Number(key)))
                                .map(key => resp[key]);

                            return successContentCustomersV1({ data });
                        }),
                        catchError((err: Error) => of(errorContentCustomersV1({ payload: err })))
                    )
            )
        )
    );

    public rateOurAnswer$ = createEffect(
        () => this.actions$.pipe(
            ofType(rateOurAnswer),
            switchMap(
                (actions) => this.httpS.executeRequestAzureCA(GESTION_CONTENIDO_ENDPOINTS.post_rate_our_answer, { ...actions }, this.httpS.getAzureCAUrl(AZURE_CA_URLS.CONTENIDO))
                    .pipe(
                        map(resp => successRateOurAnswer({ data: resp })),
                        catchError((err: Error) => of(errorRateOurAnswer({ payload: err })))
                    )
            )
        )
    );

    public getMuralData$ = createEffect(
        () => this.actions$.pipe(
            ofType(getMuralData),
            switchMap(
                () => this.httpS.executeRequestAzureCA(GESTION_CONTENIDO_ENDPOINTS.post_get_mural_data, null, this.httpS.getAzureCAUrl(AZURE_CA_URLS.CONTENIDO))
                    .pipe(
                        map(resp => successGetMuralData(resp)),
                        catchError((err: Error) => of(errorGetMuralData({ payload: err })))
                    )
            )
        )
    );

    public getPrivacyNotice$ = createEffect(
        () => this.actions$.pipe(
            ofType(getPrivacyNotice),
            switchMap(
                () => this.httpS.executeRequestByUser(GESTION_CONTENIDO_ENDPOINTS.post_get_privacy_notice, null, this.httpS.getAzureCAUrl(AZURE_CA_URLS.CONTENIDO))
                    .pipe(
                        map(resp => successGetPrivacyNotice(resp)),
                        catchError((err: Error) => of(errorGetPrivacyNotice({ payload: err })))
                    )
            )
        )
    );

    public getContactInfo$ = createEffect(
        () => this.actions$.pipe(
            ofType(getContactInfo),
            switchMap(
                () => this.httpS.executeRequestByUser(GESTION_CONTENIDO_ENDPOINTS.post_get_contact_info, null, this.httpS.getAzureCAUrl(AZURE_CA_URLS.CONTENIDO))
                    .pipe(
                        map(resp => {
                            const data = Array.isArray(resp.data) ? resp.data : Object
                                .keys(resp.data)
                                .filter(key => !isNaN(Number(key)))
                                .map(key => resp.data[key]);
                            return successGetContactInfo({ data });
                        }),
                        catchError((err: Error) => of(errorGetContactInfo({ payload: err })))
                    )
            )
        )
    );

    public getInicioApp$ = createEffect(
        () => this.actions$.pipe(
            ofType(getInicioApp),
            switchMap(
                () => this.httpS.executeRequestByUser(GESTION_CONTENIDO_ENDPOINTS.post_get_inicio_app, null, this.httpS.getAzureCAUrl(AZURE_CA_URLS.CONTENIDO))
                    .pipe(
                        map(resp => successGetInicioApp(resp)),
                        catchError((err: Error) => of(errorGetInicioApp({ payload: err })))
                    )
            )
        )
    );

    public getMechanicsProgram$ = createEffect(
        () => this.actions$.pipe(
            ofType(getMechanicsProgram),
            switchMap(
                () => this.httpS.executeRequestByUser(GESTION_CONTENIDO_ENDPOINTS.post_get_mechanics_program, null, this.httpS.getAzureCAUrl(AZURE_CA_URLS.CONTENIDO))
                    .pipe(
                        map(resp => successGetMechanicsProgram({ data: resp })),
                        catchError((err: Error) => of(errorGetMechanicsProgram({ payload: err })))
                    )
            )
        )
    );

    public getTermsAndConditions$ = createEffect(
        () => this.actions$.pipe(
            ofType(getTermsAndConditions),
            switchMap(
                () => this.httpS.executeRequestAzureCA(GESTION_CONTENIDO_ENDPOINTS.post_get_terms_and_conditions, null, this.httpS.getAzureCAUrl(AZURE_CA_URLS.CONTENIDO))
                    .pipe(
                        map(resp => successGetTermsAndConditions(resp)),
                        catchError((err: Error) => of(errorGetTermsAndConditions({ payload: err })))
                    )
            )
        )
    );

    public getQuestions$ = createEffect(
        () => this.actions$.pipe(
            ofType(getQuestions),
            switchMap(
                () => this.httpS.executeRequestByUser(GESTION_CONTENIDO_ENDPOINTS.post_get_questions, null, this.httpS.getAzureCAUrl(AZURE_CA_URLS.CONTENIDO))
                    .pipe(
                        map(resp => {
                            const data: Preguntas[] = Array.isArray(resp) ? resp : Object
                                .keys(resp)
                                .filter(key => !isNaN(Number(key)))
                                .map(key => resp[key]);
                            return successGetQuestions({ data });
                        }),
                        catchError((err: Error) => of(errorGetQuestions({ payload: err })))
                    )
            )
        )
    );
}
